import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

interface LanguageProviderProps {
  children: ReactNode;
}

// Translation keys and values
const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.logout': 'Logout',
    'nav.profile': 'My Profile',
    'nav.dashboard': 'My Dashboard',
    'nav.marketplace': 'Marketplace',
    'nav.businessPlan': 'Business Plan',
    'nav.adminDashboard': 'Admin Dashboard',
    'nav.back': 'Back',
    
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.submit': 'Submit',
    'common.update': 'Update',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.all': 'All',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.optional': 'optional',
    'common.required': 'required',
    'common.retry': 'Retry',
    
    // Language
    'language': 'en',
    
    // Authentication
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmEmail': 'Confirm Email',
    'auth.username': 'Username',
    'auth.forgotPassword': 'Forgot password?',
    'auth.dontHaveAccount': "Don't have an account?",
    'auth.alreadyHaveAccount': 'Already have an account?',
    'auth.loggingIn': 'Logging in...',
    'auth.creatingAccount': 'Creating Account...',
    'auth.iAmA': 'I am a:',
    'auth.businessEntrepreneur': 'Business/Entrepreneur',
    'auth.aiSpecialist': 'AI Specialist',
    'auth.acceptTerms': 'I accept the',
    'auth.termsOfService': 'Terms of Service',
    'auth.and': 'and',
    'auth.privacyPolicy': 'Privacy Policy',
    
    // Profile
    'profile.requesterProfile': 'Requester Profile',
    'profile.providerProfile': 'Provider Profile',
    'profile.companyName': 'Company Name',
    'profile.contactName': 'Contact Name',
    'profile.phoneNumber': 'Phone Number',
    'profile.streetAddress': 'Street Address',
    'profile.streetAddressPlaceholder': 'Number and street name',
    'profile.zipCode': 'ZIP Code',
    'profile.city': 'City',
    'profile.country': 'Country',
    'profile.preferredLanguage': 'Preferred Language',
    'profile.selectCountry': 'Select a country',
    'profile.selectLanguage': 'Select a language',
    'profile.majorEuropeanCountries': 'Major European Countries',
    'profile.otherCountries': 'Other Countries',
    'profile.companyInfo': 'Company Information',
    'profile.contactInfo': 'Contact Information',
    'profile.skillsExpertise': 'Skills and Expertise',
    'profile.businessDescription': 'Business Description',
    'profile.shortDescription': 'Short Description',
    'profile.detailedDescription': 'Detailed Description',
    'profile.skills': 'Skills',
    'profile.addSkills': 'add at least 3',
    'profile.separateSkills': 'Separate multiple skills with commas',
    'profile.addSkillsPlaceholder': 'e.g., Machine Learning, NLP, Computer Vision',
    'profile.addButton': 'Add',
    'profile.saving': 'Saving...',
    'profile.saveProfile': 'Save Profile',
    'profile.firstName': 'First Name',
    'profile.lastName': 'Last Name',
    'profile.roleInCompany': 'Role in Company',
    'profile.employees': 'Number of Employees',
    'profile.selectEmployees': 'Select',
    'profile.justMe': 'Just me',
    'profile.naceCode': 'NACE Code',
    'profile.maxCharacters': 'max 200 characters',
    'profile.characters': 'characters',
    'profile.skillsAlreadyAdded': 'These skills are already added',
    'profile.addAtLeastThreeSkills': 'Please add at least 3 skills',
    
    // Profile Images
    'profile.images': 'Profile Images',
    'profile.userPhoto': 'Profile Photo',
    'profile.companyLogo': 'Company Logo',
    'profile.userPhotoDesc': 'Upload a professional photo of yourself',
    'profile.companyLogoDesc': 'Upload your company logo',
    'profile.userPhotoRecommended': 'recommended',
    'profile.companyLogoOptional': 'optional',
    'profile.imageRequirements': 'Recommended: {size} pixels',
    'profile.maxFileSize': 'Max file size: 10MB',
    'profile.supportedFormats': 'Formats: JPEG, PNG, WebP, GIF',
    'profile.squareImagesWork': 'Square images work best for profile photos',
    'profile.changeImage': 'Change {type}',
    'profile.uploadImage': 'Upload {type}',
    'profile.processing': 'Processing...',
    'profile.removeImage': 'Remove image',
    
    // Projects
    'project.createNew': 'Create New Project',
    'project.projectName': 'Project Name',
    'project.shortDesc': 'Short Description',
    'project.longDesc': 'Detailed Description',
    'project.budget': 'Budget',
    'project.timeline': 'Implementation Timeline',
    'project.selectTimeline': 'Select timeline',
    'project.timeline1': '1 month',
    'project.timeline2-3': '2-3 months',
    'project.timeline3-6': '3-6 months',
    'project.timeline6+': 'More than 6 months',
    'project.timelineUnknown': "Don't know yet",
    'project.creating': 'Creating...',
    'project.createProject': 'Create Project',
    'project.myProjects': 'My Projects',
    'project.newProject': 'New Project',
    'project.noProjects': 'No projects yet',
    'project.createFirst': 'Create your first project to start connecting with AI specialists.',
    'project.createFirstProject': 'Create First Project',
    'project.viewDetails': 'View Details',
    'project.status': 'Status',
    'project.active': 'Active',
    'project.closed': 'Closed',
    'project.completed': 'Completed',
    'project.upcoming': 'Upcoming',
    'project.archived': 'Archived',
    'project.views': 'views',
    'project.proposals': 'Proposals',
    'project.description': 'Project Description',
    'project.detailedDescription': 'Detailed Description',
    'project.created': 'Created',
    'project.lastUpdated': 'Last updated',
    'project.updatedOn': 'Updated on',
    'project.submittedOn': 'Submitted on',
    
    // Marketplace
    'marketplace.title': 'Marketplace',
    'marketplace.allCountries': 'All Countries',
    'marketplace.noSubscription': "You don't have an active subscription.",
    'marketplace.selectPlan': 'Select a plan',
    'marketplace.accessMore': 'to access more projects.',
    'marketplace.normalTierLimit': 'You can only see projects from',
    'marketplace.upgradePlan': 'Upgrade your plan',
    'marketplace.accessOtherCountries': 'to access projects from other countries.',
    'marketplace.noProjects': 'No projects available',
    'marketplace.checkLater': 'Check back later for new projects.',
    'marketplace.browseProjects': 'Browse Projects',
    'marketplace.chatWithRequester': 'Chat with requester',
    'marketplace.youHaveMessage': 'You have a message',
    'marketplace.searchPlaceholder': 'Search projects by title or description...',
    'marketplace.searchResults': 'Search results for',
    'marketplace.projectsFound': 'projects found',
    'marketplace.noSearchResults': 'No projects found',
    'marketplace.tryDifferentSearch': 'Try a different search term or clear the search to see all projects.',
    'marketplace.clearSearch': 'Clear Search',
    
    // Business Plan
    'businessPlan.title': 'Choose Your Business Plan',
    'businessPlan.subtitle': 'Select the plan that best fits your needs. All plans include access to our platform\'s core features.',
    'businessPlan.free': 'Free',
    'businessPlan.normal': 'Normal',
    'businessPlan.gold': 'Gold',
    'businessPlan.platinum': 'Platinum',
    'businessPlan.month': '/month',
    'businessPlan.getStarted': 'Get Started',
    'businessPlan.currentPlan': 'Current Plan',
    'businessPlan.selectPlan': 'Select Plan',
    'businessPlan.renewsOn': 'Your current subscription will renew on',
    'businessPlan.specialOffer': 'Launch Edition',
    'businessPlan.features.normal.projects': 'Access to 5 projects',
    'businessPlan.features.normal.country': 'Projects in your country only',
    'businessPlan.features.normal.visibility': 'Basic profile visibility',
    'businessPlan.features.gold.projects': 'Access to 10 projects',
    'businessPlan.features.gold.countries': 'Projects in multiple countries',
    'businessPlan.features.gold.visibility': 'Enhanced profile visibility',
    'businessPlan.features.platinum.projects': 'Unlimited projects',
    'businessPlan.features.platinum.countries': 'Projects in all countries',
    'businessPlan.features.platinum.support': 'Priority profile placement',
    
    // Chat
    'chat.typeMessage': 'Type your message...',
    'chat.send': 'Send',
    'chat.chatWith': 'Chat with',
    'chat.answerToRequester': 'Answer to Requester',
    'chat.openChat': 'Open Chat',
    'chat.chat': 'Chat',
    
    // Proposals
    'proposal.submit': 'Submit Proposal',
    'proposal.update': 'Update Proposal',
    'proposal.message': 'Proposal Message',
    'proposal.messageDesc': 'Describe your approach, experience, and why you\'re the best fit for this project...',
    'proposal.minChars': 'Minimum 50 characters required. Current length:',
    'proposal.messageRequired': 'Please enter a proposal message',
    'proposal.minCharsError': 'Your proposal message should be at least 50 characters long',
    'proposal.maxCharsError': 'Your proposal message should not exceed 500 words (2500 characters)',
    'proposal.submitting': 'Submitting...',
    'proposal.updating': 'Updating...',
    'proposal.pending': 'Pending',
    'proposal.accepted': 'Accepted',
    'proposal.rejected': 'Rejected',
    'proposal.submitted': 'Submitted',
    'proposal.accept': 'Accept',
    'proposal.reject': 'Reject',
    'proposal.history': 'Proposal History',
    'proposal.updatesHistory': 'Updates History',
    'proposal.submitFor': 'Submit Proposal for',
    
    // Dashboard
    'dashboard.provider': 'Provider Dashboard',
    'dashboard.requester': 'Requester Dashboard',
    'dashboard.currentPlan': 'Current Business Plan',
    'dashboard.noSubscription': 'No active subscription',
    'dashboard.validUntil': 'Valid until:',
    'dashboard.changePlan': 'Change Plan',
    'dashboard.submitted': 'Submitted on',
    
    // Submissions
    'submissions.mySubmissions': 'My Submissions',
    'submissions.totalSubmissions': 'Total Submissions',
    'submissions.pendingSubmissions': 'Pending Submissions',
    'submissions.acceptedSubmissions': 'Accepted Submissions',
    'submissions.noSubmissions': 'No submissions yet',
    'submissions.browseMarketplace': 'Browse the marketplace to find projects and submit proposals.',
    'submissions.update': 'update',
    'submissions.updates': 'updates',
    'submissions.youGotMessage': 'You\'ve got a message',
    'submissions.lastMessage': 'Last message',
    'submissions.noMessages': 'No messages yet',
    'submissions.submittedOn': 'Submitted on',
    'submissions.noPendingSubmissions': 'No pending submissions found',
    'submissions.noAcceptedSubmissions': 'No accepted submissions found',
    
    // Admin
    'admin.dashboard': 'Admin Dashboard',
    'admin.users': 'Users',
    'admin.manageUsers': 'Manage Users',
    'admin.totalUsers': 'Total Users',
    'admin.requesters': 'Requesters',
    'admin.providers': 'Providers',
    'admin.projects': 'Projects',
    'admin.totalProjects': 'Total Projects',
    'admin.activeProjects': 'Active',
    'admin.completedProjects': 'Completed',
    'admin.subscriptionPrices': 'Subscription Prices',
    'admin.editPrices': 'Edit Prices',
    'admin.regular': 'Regular',
    'admin.promotional': 'Promotional',
    'admin.usersByCountry': 'Users by Country',
    'admin.backToDashboard': 'Back to Dashboard',
    'admin.user': 'User',
    'admin.role': 'Role',
    'admin.joined': 'Joined',
    'admin.actions': 'Actions',
    'admin.kpiDashboard': 'KPI Dashboard',
    'admin.userMetrics': 'User Metrics',
    'admin.projectMetrics': 'Project Metrics',
    'admin.proposalMetrics': 'Proposal Metrics',
    'admin.adminProfile': 'Admin Profile',
    'admin.emailAddress': 'Email Address',
    'admin.displayName': 'Display Name',
    'admin.phoneNumber': 'Phone Number',
    'admin.department': 'Department',
    'admin.lastLogin': 'Last Login',
    'admin.saveChanges': 'Save Changes',
    'admin.saving': 'Saving...',
    'admin.systemMessages': 'System Messages',
    'admin.addMessage': 'Add Message',
    'admin.enterNewMessage': 'Enter new message...',
    'admin.created': 'Created',
    'admin.active': 'Active',
    'admin.inactive': 'Inactive',
    'admin.noMessages': 'No messages yet',
    'admin.deleteMessage': 'Are you sure you want to delete this message?',
    'admin.messageCreated': 'Message created successfully',
    'admin.messageUpdated': 'Message updated successfully',
    'admin.messageDeleted': 'Message deleted successfully',
    'admin.messageActivated': 'activated',
    'admin.messageDeactivated': 'deactivated',
    'admin.messageStatusUpdated': 'successfully',
    'admin.errorCreatingMessage': 'Error creating message',
    'admin.errorUpdatingMessage': 'Error updating message',
    'admin.errorDeletingMessage': 'Error deleting message',
    'admin.errorUpdatingStatus': 'Error updating message status',
    'admin.errorLoadingMessages': 'Error loading messages',
    'admin.errorLoadingUsers': 'Error loading users',
    'admin.userRoleUpdated': 'User role updated successfully',
    'admin.errorUpdatingRole': 'Error updating user role',
    'admin.errorLoadingMetrics': 'Error loading metrics',
    'admin.errorLoadingProfile': 'Error loading profile',
    'admin.errorUpdatingProfile': 'Error updating profile',
    'admin.profileUpdated': 'Profile updated successfully',
    'admin.pricesUpdated': 'Subscription prices updated successfully',
    'admin.errorUpdatingPrices': 'Error updating subscription prices',
    'admin.errorLoadingDashboard': 'Error loading dashboard data',
    
    // Advertising
    'advertising.title': 'Advertising',
    'advertising.adminTitle': 'Advertising Management',
    'advertising.createAdvertisement': 'Create Advertisement',
    'advertising.myAdvertisements': 'My Advertisements',
    'advertising.allAdvertisements': 'All Advertisements',
    'advertising.noAdvertisements': 'No advertisements yet',
    'advertising.pricingInfo': 'Pricing Information',
    'advertising.pricingManagement': 'Pricing Management',
    'advertising.adTitle': 'Advertisement Title',
    'advertising.titlePlaceholder': 'Enter a compelling title for your ad',
    'advertising.description': 'Description',
    'advertising.descriptionPlaceholder': 'Describe your company and services',
    'advertising.descriptionTooLong': 'Description must be 200 characters or less',
    'advertising.websiteUrl': 'Website URL',
    'advertising.image': 'Image',
    'advertising.chooseImage': 'Choose Image',
    'advertising.imageRequirements': 'Max 2MB, JPG/PNG format',
    'advertising.fileTooLarge': 'File size must be less than 2MB',
    'advertising.invalidFileType': 'Please select a valid image file',
    'advertising.duration': 'Duration',
    'advertising.oneWeek': '1 Week',
    'advertising.twoWeeks': '2 Weeks',
    'advertising.threeWeeks': '3 Weeks',
    'advertising.week': 'week',
    'advertising.weeks': 'weeks',
    'advertising.totalPrice': 'Total Price',
    'advertising.submitAdvertisement': 'Submit Advertisement',
    'advertising.submitting': 'Submitting...',
    'advertising.submittedSuccessfully': 'Advertisement submitted successfully',
    'advertising.fillRequiredFields': 'Please fill in all required fields',
    'advertising.accessDenied': 'Access Denied',
    'advertising.providersOnly': 'Only providers can create advertisements',
    'advertising.approve': 'Approve',
    'advertising.reject': 'Reject',
    'advertising.approvedSuccessfully': 'Advertisement approved successfully',
    'advertising.rejectedSuccessfully': 'Advertisement rejected successfully',
    'advertising.pricesUpdated': 'Advertising prices updated successfully',
    'advertising.status.pending': 'Pending',
    'advertising.status.approved': 'Approved',
    'advertising.status.active': 'Active',
    'advertising.status.rejected': 'Rejected',
    'advertising.status.expired': 'Expired',
    
    // Errors and Messages
    'error.general': 'An error occurred. Please try again.',
    'error.loading': 'Error loading data',
    'error.saving': 'Error saving data',
    'error.updating': 'Error updating data',
    'error.userNotFound': 'Error loading profile: User not found',
    'error.accessDenied': 'Access denied: Not a requester profile',
    'error.projectNotFound': 'Project Not Found',
    'error.projectNotFoundDesc': 'The project you\'re looking for doesn\'t exist or has been removed.',
    'success.saved': 'Saved successfully',
    'success.updated': 'Updated successfully',
    'success.created': 'Created successfully',
    'success.deleted': 'Deleted successfully',
    'success.proposalAccepted': 'Proposal accepted successfully',
    'success.proposalRejected': 'Proposal rejected successfully',
    
    // Home page
    'home.hero.title': 'Connect Entrepreneurs with AI Specialists',
    'home.hero.subtitle': 'OpportunAI brings together businesses seeking AI solutions with the experts who can deliver them.',
    'home.hero.registerNow': 'Register Now',
    'home.hero.learnMore': 'Learn More',
    'home.howItWorks': 'How OpportunAI Works',
    'home.forEntrepreneurs': 'For Entrepreneurs',
    'home.forEntrepreneurs.desc': 'Create your profile, post your AI project needs, and connect with specialized providers.',
    'home.forSpecialists': 'For AI Specialists',
    'home.forSpecialists.desc': 'Browse projects, submit proposals, and connect with businesses needing your expertise.',
    'home.secureConnection': 'Secure Connection',
    'home.secureConnection.desc': 'Our platform ensures secure, efficient matching between businesses and AI providers.',
    'home.viewPlans': 'View Plans',
    'home.subscriptionPlans': 'Subscription Plans',
    'home.subscriptionPlans.desc': 'Choose the right plan for your business and access AI projects across different regions.',
    'home.footer.description': 'Connecting artisans, merchants and independents with AI specialists to drive innovation and growth.',
    'home.footer.quickLinks': 'Quick Links',
    'home.footer.contact': 'Contact',
    'home.footer.contactDesc': 'Have questions about OpportunAI? Reach out to our support team.',
    'home.footer.rights': 'All rights reserved.',
    
    // Community Section
    'home.community.title': 'Join Our Community',
    'home.community.description': 'Connect with innovative artisans, merchants and independents and skilled AI specialists in a collaborative environment. Our platform brings together passionate professionals who are shaping the future of technology.',
    'home.community.activeCommunity': 'Active Community',
    'home.community.expertNetwork': 'Expert Network',
    'home.community.aiInnovation': 'AI Innovation',

    // Hero v2
    'home.hero.v2.title.line1': 'AI works for your trade.',
    'home.hero.v2.title.line2': 'Not the other way around.',
    'home.hero.v2.subtitle.line1': 'Artisan, merchant, freelancer —',
    'home.hero.v2.subtitle.line2': 'describe your need, an AI expert responds.',
    'home.hero.v2.cta': 'Post my project for free',
    'home.hero.v2.cta2': 'I offer my AI services',
    'home.hero.v2.login': 'Already have an account? Log in',

    // Stats bar
    'home.stats.free': 'Free project posting',
    'home.stats.experts': 'Vetted AI Experts',
    'home.stats.noCommitment': 'No commitment',

    // Role-based registration
    'register.role.demandeur.title': 'Welcome, pro!',
    'register.role.demandeur.subtitle': "Describe your need, an AI expert responds. It's free.",
    'register.role.demandeur.professionLabel': 'Your trade',
    'register.role.demandeur.professionPlaceholder': 'E.g.: Electrician, Painter, Florist…',
    'register.role.demandeur.submit': 'Post my first project',
    'register.role.prestataire.title': 'Welcome, expert!',
    'register.role.prestataire.subtitle': 'Access projects from artisans and pros who need you.',
    'register.role.prestataire.professionLabel': 'Your AI specialty',
    'register.role.prestataire.professionPlaceholder': 'E.g.: n8n Automation, Chatbot, AI Agents…',
    'register.role.prestataire.submit': 'Access available projects',
  },
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.login': 'Connexion',
    'nav.register': 'Inscription',
    'nav.logout': 'Déconnexion',
    'nav.profile': 'Mon Profil',
    'nav.dashboard': 'Mon Tableau de Bord',
    'nav.marketplace': 'Place de Marché',
    'nav.businessPlan': 'Plan d\'Affaires',
    'nav.adminDashboard': 'Tableau de Bord Admin',
    'nav.back': 'Retour',
    
    // Common
    'common.loading': 'Chargement...',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.submit': 'Soumettre',
    'common.update': 'Mettre à jour',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.view': 'Voir',
    'common.close': 'Fermer',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.all': 'Tous',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.optional': 'optionnel',
    'common.required': 'requis',
    'common.retry': 'Réessayer',
    
    // Language
    'language': 'fr',
    
    // Authentication
    'auth.login': 'Connexion',
    'auth.register': 'Inscription',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.confirmEmail': 'Confirmer l\'email',
    'auth.username': 'Nom d\'utilisateur',
    'auth.forgotPassword': 'Mot de passe oublié ?',
    'auth.dontHaveAccount': 'Vous n\'avez pas de compte ?',
    'auth.alreadyHaveAccount': 'Vous avez déjà un compte ?',
    'auth.loggingIn': 'Connexion en cours...',
    'auth.creatingAccount': 'Création du compte...',
    'auth.iAmA': 'Je suis un(e) :',
    'auth.businessEntrepreneur': 'Entreprise/Entrepreneur',
    'auth.aiSpecialist': 'Spécialiste IA',
    'auth.acceptTerms': 'J\'accepte les',
    'auth.termsOfService': 'Conditions Générales d\'Utilisation',
    'auth.and': 'et la',
    'auth.privacyPolicy': 'Politique de Confidentialité',
    
    // Profile
    'profile.requesterProfile': 'Profil Demandeur',
    'profile.providerProfile': 'Profil Fournisseur',
    'profile.companyName': 'Nom de l\'entreprise',
    'profile.contactName': 'Nom du contact',
    'profile.phoneNumber': 'Numéro de téléphone',
    'profile.streetAddress': 'Adresse',
    'profile.streetAddressPlaceholder': 'Numéro et nom de rue',
    'profile.zipCode': 'Code postal',
    'profile.city': 'Ville',
    'profile.country': 'Pays',
    'profile.countryOfIncorporation': 'Pays d\'incorporation',
    'profile.preferredLanguage': 'Langue préférée',
    'profile.selectCountry': 'Sélectionner un pays',
    'profile.selectLanguage': 'Sélectionner une langue',
    'profile.majorEuropeanCountries': 'Principaux pays européens',
    'profile.otherCountries': 'Autres pays',
    'profile.companyInfo': 'Informations de l\'entreprise',
    'profile.contactInfo': 'Informations de contact',
    'profile.skillsExpertise': 'Compétences et expertise',
    'profile.businessDescription': 'Description de l\'entreprise',
    'profile.shortDescription': 'Description courte',
    'profile.detailedDescription': 'Description détaillée',
    'profile.skills': 'Compétences',
    'profile.addSkills': 'ajoutez au moins 3',
    'profile.separateSkills': 'Séparez les compétences multiples par des virgules',
    'profile.addSkillsPlaceholder': 'ex: Machine Learning, NLP, Vision par ordinateur',
    'profile.addButton': 'Ajouter',
    'profile.saving': 'Enregistrement...',
    'profile.saveProfile': 'Enregistrer le profil',
    'profile.firstName': 'Prénom',
    'profile.lastName': 'Nom',
    'profile.roleInCompany': 'Rôle dans l\'entreprise',
    'profile.employees': 'Nombre d\'employés',
    'profile.selectEmployees': 'Sélectionner',
    'profile.justMe': 'Juste moi',
    'profile.naceCode': 'Code NACE',
    'profile.maxCharacters': 'max 200 caractères',
    'profile.characters': 'caractères',
    'profile.skillsAlreadyAdded': 'Ces compétences sont déjà ajoutées',
    'profile.addAtLeastThreeSkills': 'Veuillez ajouter au moins 3 compétences',
    
    // Profile Images
    'profile.images': 'Images de Profil',
    'profile.userPhoto': 'Photo de Profil',
    'profile.companyLogo': 'Logo de l\'Entreprise',
    'profile.userPhotoDesc': 'Téléchargez une photo professionnelle de vous',
    'profile.companyLogoDesc': 'Téléchargez le logo de votre entreprise',
    'profile.userPhotoRecommended': 'recommandé',
    'profile.companyLogoOptional': 'optionnel',
    'profile.imageRequirements': 'Recommandé : {size} pixels',
    'profile.maxFileSize': 'Taille max : 10MB',
    'profile.supportedFormats': 'Formats : JPEG, PNG, WebP, GIF',
    'profile.squareImagesWork': 'Les images carrées fonctionnent mieux pour les photos de profil',
    'profile.changeImage': 'Changer {type}',
    'profile.uploadImage': 'Télécharger {type}',
    'profile.processing': 'Traitement...',
    'profile.removeImage': 'Supprimer l\'image',
    
    // Projects
    'project.createNew': 'Créer un nouveau projet',
    'project.projectName': 'Nom du projet',
    'project.shortDesc': 'Description courte',
    'project.longDesc': 'Description détaillée',
    'project.budget': 'Budget',
    'project.timeline': 'Délai de mise en œuvre',
    'project.selectTimeline': 'Sélectionner le délai',
    'project.timeline1': '1 mois',
    'project.timeline2-3': '2-3 mois',
    'project.timeline3-6': '3-6 mois',
    'project.timeline6+': 'Plus de 6 mois',
    'project.timelineUnknown': 'Je ne sais pas encore',
    'project.creating': 'Création...',
    'project.createProject': 'Créer le projet',
    'project.myProjects': 'Mes projets',
    'project.newProject': 'Nouveau projet',
    'project.noProjects': 'Aucun projet pour le moment',
    'project.createFirst': 'Créez votre premier projet pour commencer à vous connecter avec des spécialistes IA.',
    'project.createFirstProject': 'Créer le premier projet',
    'project.viewDetails': 'Voir les détails',
    'project.status': 'Statut',
    'project.active': 'Actif',
    'project.closed': 'Fermé',
    'project.completed': 'Terminé',
    'project.upcoming': 'À venir',
    'project.archived': 'Archivé',
    'project.views': 'vues',
    'project.proposals': 'Propositions',
    'project.description': 'Description du projet',
    'project.detailedDescription': 'Description détaillée',
    'project.created': 'Créé',
    'project.lastUpdated': 'Dernière mise à jour',
    'project.updatedOn': 'Mis à jour le',
    'project.submittedOn': 'Soumis le',
    
    // Marketplace
    'marketplace.title': 'Place de marché',
    'marketplace.allCountries': 'Tous les pays',
    'marketplace.noSubscription': 'Vous n\'avez pas d\'abonnement actif.',
    'marketplace.selectPlan': 'Sélectionner un plan',
    'marketplace.accessMore': 'pour accéder à plus de projets.',
    'marketplace.normalTierLimit': 'Vous ne pouvez voir que les projets de',
    'marketplace.upgradePlan': 'Améliorez votre plan',
    'marketplace.accessOtherCountries': 'pour accéder aux projets d\'autres pays.',
    'marketplace.noProjects': 'Aucun projet disponible',
    'marketplace.checkLater': 'Revenez plus tard pour de nouveaux projets.',
    'marketplace.browseProjects': 'Parcourir les projets',
    'marketplace.chatWithRequester': 'Discuter avec le demandeur',
    'marketplace.youHaveMessage': 'Vous avez un message',
    'marketplace.searchPlaceholder': 'Rechercher des projets par titre ou description...',
    'marketplace.searchResults': 'Résultats de recherche pour',
    'marketplace.projectsFound': 'projets trouvés',
    'marketplace.noSearchResults': 'Aucun projet trouvé',
    'marketplace.tryDifferentSearch': 'Essayez un terme de recherche différent ou effacez la recherche pour voir tous les projets.',
    'marketplace.clearSearch': 'Effacer la recherche',
    
    // Business Plan
    'businessPlan.title': 'Choisissez votre plan d\'affaires',
    'businessPlan.subtitle': 'Sélectionnez le plan qui correspond le mieux à vos besoins. Tous les plans incluent l\'accès aux fonctionnalités principales de notre plateforme.',
    'businessPlan.free': 'Gratuit',
    'businessPlan.normal': 'Normal',
    'businessPlan.gold': 'Or',
    'businessPlan.platinum': 'Platine',
    'businessPlan.month': '/mois',
    'businessPlan.getStarted': 'Commencer',
    'businessPlan.currentPlan': 'Plan actuel',
    'businessPlan.selectPlan': 'Sélectionner le plan',
    'businessPlan.renewsOn': 'Votre abonnement actuel sera renouvelé le',
    'businessPlan.specialOffer': 'Offre de lancement',
    'businessPlan.features.normal.projects': 'Accès à 5 projets',
    'businessPlan.features.normal.country': 'Projets dans votre pays uniquement',
    'businessPlan.features.normal.visibility': 'Visibilité de profil de base',
    'businessPlan.features.gold.projects': 'Accès à 10 projets',
    'businessPlan.features.gold.countries': 'Projets dans plusieurs pays',
    'businessPlan.features.gold.visibility': 'Visibilité de profil améliorée',
    'businessPlan.features.platinum.projects': 'Projets illimités',
    'businessPlan.features.platinum.countries': 'Projets dans tous les pays',
    'businessPlan.features.platinum.support': 'Placement de profil prioritaire',
    
    // Chat
    'chat.typeMessage': 'Tapez votre message...',
    'chat.send': 'Envoyer',
    'chat.chatWith': 'Discuter avec',
    'chat.answerToRequester': 'Répondre au demandeur',
    'chat.openChat': 'Ouvrir le chat',
    'chat.chat': 'Discuter',
    
    // Proposals
    'proposal.submit': 'Soumettre la proposition',
    'proposal.update': 'Mettre à jour la proposition',
    'proposal.message': 'Message de proposition',
    'proposal.messageDesc': 'Décrivez votre approche, votre expérience et pourquoi vous êtes le meilleur choix pour ce projet...',
    'proposal.minChars': 'Minimum 50 caractères requis. Longueur actuelle :',
    'proposal.messageRequired': 'Veuillez entrer un message de proposition',
    'proposal.minCharsError': 'Votre message de proposition doit contenir au moins 50 caractères',
    'proposal.maxCharsError': 'Votre message de proposition ne doit pas dépasser 500 mots (2500 caractères)',
    'proposal.submitting': 'Soumission...',
    'proposal.updating': 'Mise à jour...',
    'proposal.pending': 'En attente',
    'proposal.accepted': 'Accepté',
    'proposal.rejected': 'Rejeté',
    'proposal.submitted': 'Soumis',
    'proposal.accept': 'Accepter',
    'proposal.reject': 'Rejeter',
    'proposal.history': 'Historique des propositions',
    'proposal.updatesHistory': 'Historique des mises à jour',
    'proposal.submitFor': 'Soumettre une proposition pour',
    
    // Dashboard
    'dashboard.provider': 'Tableau de bord fournisseur',
    'dashboard.requester': 'Tableau de bord demandeur',
    'dashboard.currentPlan': 'Plan d\'affaires actuel',
    'dashboard.noSubscription': 'Aucun abonnement actif',
    'dashboard.validUntil': 'Valide jusqu\'au :',
    'dashboard.changePlan': 'Changer de plan',
    'dashboard.submitted': 'Soumis le',
    
    // Submissions
    'submissions.mySubmissions': 'Mes soumissions',
    'submissions.totalSubmissions': 'Total des soumissions',
    'submissions.pendingSubmissions': 'Soumissions en attente',
    'submissions.acceptedSubmissions': 'Soumissions acceptées',
    'submissions.noSubmissions': 'Aucune soumission pour le moment',
    'submissions.browseMarketplace': 'Parcourez la place de marché pour trouver des projets et soumettre des propositions.',
    'submissions.update': 'mise à jour',
    'submissions.updates': 'mises à jour',
    'submissions.youGotMessage': 'Vous avez reçu un message',
    'submissions.lastMessage': 'Dernier message',
    'submissions.noMessages': 'Aucun message pour le moment',
    'submissions.submittedOn': 'Soumis le',
    'submissions.noPendingSubmissions': 'Aucune soumission en attente trouvée',
    'submissions.noAcceptedSubmissions': 'Aucune soumission acceptée trouvée',
    
    // Admin
    'admin.dashboard': 'Tableau de bord admin',
    'admin.users': 'Utilisateurs',
    'admin.manageUsers': 'Gérer les utilisateurs',
    'admin.totalUsers': 'Total utilisateurs',
    'admin.requesters': 'Demandeurs',
    'admin.providers': 'Fournisseurs',
    'admin.projects': 'Projets',
    'admin.totalProjects': 'Total projets',
    'admin.activeProjects': 'Actifs',
    'admin.completedProjects': 'Terminés',
    'admin.subscriptionPrices': 'Prix des abonnements',
    'admin.editPrices': 'Modifier les prix',
    'admin.regular': 'Régulier',
    'admin.promotional': 'Promotionnel',
    'admin.usersByCountry': 'Utilisateurs par pays',
    'admin.backToDashboard': 'Retour au tableau de bord',
    'admin.user': 'Utilisateur',
    'admin.role': 'Rôle',
    'admin.joined': 'Inscrit',
    'admin.actions': 'Actions',
    'admin.kpiDashboard': 'Tableau de bord KPI',
    'admin.userMetrics': 'Métriques utilisateurs',
    'admin.projectMetrics': 'Métriques projets',
    'admin.proposalMetrics': 'Métriques propositions',
    'admin.adminProfile': 'Profil administrateur',
    'admin.emailAddress': 'Adresse email',
    'admin.displayName': 'Nom d\'affichage',
    'admin.phoneNumber': 'Numéro de téléphone',
    'admin.department': 'Département',
    'admin.lastLogin': 'Dernière connexion',
    'admin.saveChanges': 'Enregistrer les modifications',
    'admin.saving': 'Enregistrement...',
    'admin.systemMessages': 'Messages système',
    'admin.addMessage': 'Ajouter un message',
    'admin.enterNewMessage': 'Entrez un nouveau message...',
    'admin.created': 'Créé',
    'admin.active': 'Actif',
    'admin.inactive': 'Inactif',
    'admin.noMessages': 'Aucun message pour le moment',
    'admin.deleteMessage': 'Êtes-vous sûr de vouloir supprimer ce message ?',
    'admin.messageCreated': 'Message créé avec succès',
    'admin.messageUpdated': 'Message mis à jour avec succès',
    'admin.messageDeleted': 'Message supprimé avec succès',
    'admin.messageActivated': 'activé',
    'admin.messageDeactivated': 'désactivé',
    'admin.messageStatusUpdated': 'avec succès',
    'admin.errorCreatingMessage': 'Erreur lors de la création du message',
    'admin.errorUpdatingMessage': 'Erreur lors de la mise à jour du message',
    'admin.errorDeletingMessage': 'Erreur lors de la suppression du message',
    'admin.errorUpdatingStatus': 'Erreur lors de la mise à jour du statut du message',
    'admin.errorLoadingMessages': 'Erreur lors du chargement des messages',
    'admin.errorLoadingUsers': 'Erreur lors du chargement des utilisateurs',
    'admin.userRoleUpdated': 'Rôle utilisateur mis à jour avec succès',
    'admin.errorUpdatingRole': 'Erreur lors de la mise à jour du rôle utilisateur',
    'admin.errorLoadingMetrics': 'Erreur lors du chargement des métriques',
    'admin.errorLoadingProfile': 'Erreur lors du chargement du profil',
    'admin.errorUpdatingProfile': 'Erreur lors de la mise à jour du profil',
    'admin.profileUpdated': 'Profil mis à jour avec succès',
    'admin.pricesUpdated': 'Prix des abonnements mis à jour avec succès',
    'admin.errorUpdatingPrices': 'Erreur lors de la mise à jour des prix des abonnements',
    'admin.errorLoadingDashboard': 'Erreur lors du chargement des données du tableau de bord',
    
    // Advertising
    'advertising.title': 'Publicité',
    'advertising.adminTitle': 'Gestion de la publicité',
    'advertising.createAdvertisement': 'Créer une publicité',
    'advertising.myAdvertisements': 'Mes publicités',
    'advertising.allAdvertisements': 'Toutes les publicités',
    'advertising.noAdvertisements': 'Aucune publicité pour le moment',
    'advertising.pricingInfo': 'Informations tarifaires',
    'advertising.pricingManagement': 'Gestion des tarifs',
    'advertising.adTitle': 'Titre de la publicité',
    'advertising.titlePlaceholder': 'Entrez un titre accrocheur pour votre publicité',
    'advertising.description': 'Description',
    'advertising.descriptionPlaceholder': 'Décrivez votre entreprise et vos services',
    'advertising.descriptionTooLong': 'La description doit faire 200 caractères ou moins',
    'advertising.websiteUrl': 'URL du site web',
    'advertising.image': 'Image',
    'advertising.chooseImage': 'Choisir une image',
    'advertising.imageRequirements': 'Max 2MB, format JPG/PNG',
    'advertising.fileTooLarge': 'La taille du fichier doit être inférieure à 2MB',
    'advertising.invalidFileType': 'Veuillez sélectionner un fichier image valide',
    'advertising.duration': 'Durée',
    'advertising.oneWeek': '1 Semaine',
    'advertising.twoWeeks': '2 Semaines',
    'advertising.threeWeeks': '3 Semaines',
    'advertising.week': 'semaine',
    'advertising.weeks': 'semaines',
    'advertising.totalPrice': 'Prix total',
    'advertising.submitAdvertisement': 'Soumettre la publicité',
    'advertising.submitting': 'Soumission...',
    'advertising.submittedSuccessfully': 'Publicité soumise avec succès',
    'advertising.fillRequiredFields': 'Veuillez remplir tous les champs requis',
    'advertising.accessDenied': 'Accès refusé',
    'advertising.providersOnly': 'Seuls les fournisseurs peuvent créer des publicités',
    'advertising.approve': 'Approuver',
    'advertising.reject': 'Rejeter',
    'advertising.approvedSuccessfully': 'Publicité approuvée avec succès',
    'advertising.rejectedSuccessfully': 'Publicité rejetée avec succès',
    'advertising.pricesUpdated': 'Prix de la publicité mis à jour avec succès',
    'advertising.status.pending': 'En attente',
    'advertising.status.approved': 'Approuvé',
    'advertising.status.active': 'Actif',
    'advertising.status.rejected': 'Rejeté',
    'advertising.status.expired': 'Expiré',
    
    // Errors and Messages
    'error.general': 'Une erreur s\'est produite. Veuillez réessayer.',
    'error.loading': 'Erreur lors du chargement des données',
    'error.saving': 'Erreur lors de l\'enregistrement des données',
    'error.updating': 'Erreur lors de la mise à jour des données',
    'error.userNotFound': 'Erreur lors du chargement du profil : Utilisateur non trouvé',
    'error.accessDenied': 'Accès refusé : Pas un profil de demandeur',
    'error.projectNotFound': 'Projet non trouvé',
    'error.projectNotFoundDesc': 'Le projet que vous recherchez n\'existe pas ou a été supprimé.',
    'success.saved': 'Enregistré avec succès',
    'success.updated': 'Mis à jour avec succès',
    'success.created': 'Créé avec succès',
    'success.deleted': 'Supprimé avec succès',
    'success.proposalAccepted': 'Proposition acceptée avec succès',
    'success.proposalRejected': 'Proposition rejetée avec succès',
    
    // Home page
    'home.hero.title': 'Connecter les artisans, commerçants et indépendants avec les spécialistes IA',
    'home.hero.subtitle': 'OpportunAI rassemble les entreprises cherchant des solutions IA avec les experts qui peuvent les livrer.',
    'home.hero.registerNow': 'S\'inscrire maintenant',
    'home.hero.learnMore': 'En savoir plus',
    'home.howItWorks': 'Comment fonctionne OpportunAI',
    'home.forEntrepreneurs': 'Pour les artisans, commerçants et indépendants',
    'home.forEntrepreneurs.desc': 'Créez votre profil, publiez vos besoins de projets IA et connectez-vous avec des fournisseurs spécialisés.',
    'home.forSpecialists': 'Pour les spécialistes IA',
    'home.forSpecialists.desc': 'Parcourez les projets, soumettez des propositions et connectez-vous avec des entreprises ayant besoin de votre expertise.',
    'home.secureConnection': 'Connexion sécurisée',
    'home.secureConnection.desc': 'Notre plateforme assure une correspondance sécurisée et efficace entre les entreprises et les fournisseurs IA.',
    'home.viewPlans': 'Voir les plans',
    'home.subscriptionPlans': 'Plans d\'abonnement',
    'home.subscriptionPlans.desc': 'Choisissez le bon plan pour votre entreprise et accédez aux projets IA dans différentes régions.',
    'home.footer.description': 'Connecter les artisans, commerçants et indépendants avec les spécialistes IA pour stimuler l\'innovation et la croissance.',
    'home.footer.quickLinks': 'Liens rapides',
    'home.footer.contact': 'Contact',
    'home.footer.contactDesc': 'Vous avez des questions sur OpportunAI ? Contactez notre équipe de support.',
    'home.footer.rights': 'Tous droits réservés.',
    
    // Community Section
    'home.community.title': 'Rejoignez notre communauté',
    'home.community.description': 'Connectez-vous avec des artisans, commerçants et indépendants innovants et des spécialistes IA qualifiés dans un environnement collaboratif. Notre plateforme rassemble des professionnels passionnés qui façonnent l\'avenir de la technologie.',
    'home.community.activeCommunity': 'Communauté Active',
    'home.community.expertNetwork': 'Réseau d\'Experts',
    'home.community.aiInnovation': 'Innovation IA',

    // Hero v2
    'home.hero.v2.title.line1': 'L\'IA travaille pour ton métier.',
    'home.hero.v2.title.line2': 'Pas l\'inverse.',
    'home.hero.v2.subtitle.line1': 'Artisan, commerçant, indépendant —',
    'home.hero.v2.subtitle.line2': 'décris ton besoin, un expert IA te répond.',
    'home.hero.v2.cta': 'Je poste mon projet gratuitement',
    'home.hero.v2.cta2': 'Je propose mes services IA',
    'home.hero.v2.login': 'Déjà un compte ? Se connecter',

    // Stats bar
    'home.stats.free': 'Gratuit pour le dépôt de projet',
    'home.stats.experts': 'Experts IA sélectionnés',
    'home.stats.noCommitment': 'Sans engagement',

    // Role-based registration
    'register.role.demandeur.title': 'Bienvenue, pro !',
    'register.role.demandeur.subtitle': "Décris ton besoin, un expert IA te répond. C'est gratuit.",
    'register.role.demandeur.professionLabel': 'Ton métier',
    'register.role.demandeur.professionPlaceholder': 'Ex : Électricien, Peintre, Fleuriste…',
    'register.role.demandeur.submit': 'Je poste mon premier projet',
    'register.role.prestataire.title': 'Bienvenue, expert !',
    'register.role.prestataire.subtitle': 'Accède aux projets des artisans et pros qui ont besoin de toi.',
    'register.role.prestataire.professionLabel': 'Ta spécialité IA',
    'register.role.prestataire.professionPlaceholder': 'Ex : Automatisation n8n, Chatbot, Agents IA…',
    'register.role.prestataire.submit': 'Accéder aux projets disponibles',
  }
};

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { currentUser, userData } = useAuth();
  const [language, setLanguageState] = useState<Language>('fr');
  const [loading, setLoading] = useState(true);

  // Load user's preferred language from profile
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (!currentUser || !userData) {
        setLoading(false);
        return;
      }

      try {
        // Check user's profile for preferred language
        let profileCollection = '';
        if (userData.role === 'requester') {
          profileCollection = 'requesterProfiles';
        } else if (userData.role === 'provider') {
          profileCollection = 'providerProfiles';
        } else {
          setLoading(false);
          return;
        }

        const profileDoc = await getDoc(doc(db, profileCollection, currentUser.uid));
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const preferredLang = profileData.preferredLanguage;
          
          // Map language codes to our supported languages
          if (preferredLang === 'fr' || preferredLang === 'french') {
            setLanguageState('fr');
          } else {
            setLanguageState('en');
          }
        }
      } catch (error) {
        console.error('Error loading user language preference:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserLanguage();
  }, [currentUser, userData]);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    
    // Save language preference to user profile if logged in
    if (currentUser && userData) {
      try {
        let profileCollection = '';
        if (userData.role === 'requester') {
          profileCollection = 'requesterProfiles';
        } else if (userData.role === 'provider') {
          profileCollection = 'providerProfiles';
        }

        if (profileCollection) {
          await updateDoc(doc(db, profileCollection, currentUser.uid), {
            preferredLanguage: lang
          });
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    loading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}